"use client";

import React from "react";

import { Element as DomElement } from "html-react-parser";

import { EquationRenderer } from "./equation-renderer";

/**
 * 문단(running text) 안에 섞여 오는 인라인 수식을 MathJax로 렌더링한다.
 *
 * dpe-e2e(enhanced) 모델은 문단 텍스트 안의 수식을 `$...$`(inline) 또는 `$$...$$`(display)
 * LaTeX로 내보낸다. 하지만 뷰어는 `equation` 카테고리 요소만 렌더링해서(→ render-equation),
 * 문단 텍스트에 섞인 인라인 수식은 `$E = \frac{1}{2} m v^2$` 처럼 raw 문자열로 노출됐다.
 *
 * 텍스트 노드 변환은 html-react-parser transform이 태그 노드 위주로만 확실히 동작하므로,
 * equation/chart 처리와 동일하게 **파싱 전에** 수식 구간을 `<imath>` 커스텀 element로 감싸고
 * (→ wrapInlineMathInHtml), transform에서 element 단위로 렌더링한다(→ renderInlineMathElement).
 */

// display `$$...$$` (먼저) 와 inline `$...$`. 각 구간을 `[^$]`로 바운드해 백트래킹이 없다(선형).
const MATH_RE = /\$\$[^$]+\$\$|\$[^$\n]+\$/g;

// content.html 텍스트 구간의 엔티티를 원래 LaTeX 문자로 되돌린다(수식 안의 `<`는 `&lt;`로 옴).
const ENTITY_DECODE: Record<string, string> = {
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#34;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&amp;": "&",
};
function decodeEntities(s: string): string {
  return s.replace(
    /&(?:lt|gt|quot|apos|amp|#34|#39);/g,
    m => ENTITY_DECODE[m] ?? m,
  );
}

// <imath data-tex="..."> 속성에 안전하게 담기 위한 최소 이스케이프.
const ATTR_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  '"': "&quot;",
  "<": "&lt;",
  ">": "&gt;",
};
function escapeAttr(s: string): string {
  return s.replace(/[&"<>]/g, m => ATTR_ESCAPE[m] ?? m);
}

function isDisplay(token: string): boolean {
  return token.startsWith("$$");
}

function innerTex(token: string): string {
  return isDisplay(token) ? token.slice(2, -2) : token.slice(1, -1);
}

// `$`는 통화 기호로도 흔하다("$4,900", "$5 to $10"). `$...$` 는 내부가 수식 신호를 가질 때만
// 수식으로 취급한다: 제어열(\), 위/아래첨자(^ _), 중괄호({}), 관계·연산 기호(< > | = 등), 그리스
// 문자, 또는 `x = ...` 형태. `< > |` 를 포함해 `|x| < 1` 처럼 LaTeX 제어열이 없는 관계식도 잡는다
// (모델이 원래 `<span class="equation_inline">` 로 표시한 수식인데 earth 가 bare `$...$` 로 언랩해
// 마커가 사라지므로, 프론트에서 휴리스틱으로 복원한다). inner 는 이미 엔티티 디코드된 상태.
// `$$...$$` 는 델리미터가 명시적이라 항상 수식으로 본다.
function looksLikeMath(inner: string): boolean {
  return (
    /[\\^_{}<>|=≤≥≠±×÷√∑∫]/.test(inner) ||
    /[Α-ω]/.test(inner) || // Greek letters (α–ω, Α–Ω)
    /[A-Za-z0-9]\s*=\s*\S/.test(inner)
  );
}

function isMathToken(token: string, inner: string): boolean {
  return isDisplay(token) || looksLikeMath(inner);
}

/**
 * HTML 문자열의 텍스트 구간(태그 내부는 건드리지 않음)에서 인라인/디스플레이 수식을 찾아
 * `<imath data-tex="..." data-display="0|1"></imath>` 로 감싼다.
 */
export function wrapInlineMathInHtml(html: string): string {
  if (!html?.includes("$")) {
    return html;
  }
  // 태그 단위로 쪼갠다(짝수 index = 텍스트, 홀수 index = 태그). 태그 내부는 변환하지 않는다.
  // `[^<>]`로 태그 내부를 바운드해 백트래킹이 없다(태그는 `<`를 포함할 수 없다).
  const segments = html.split(/(<[^<>]+>)/g);
  for (let i = 0; i < segments.length; i += 2) {
    const seg = segments[i];
    if (!seg?.includes("$")) {
      continue;
    }
    segments[i] = seg.replace(MATH_RE, token => {
      // 엔티티를 먼저 디코드해 가드가 실제 문자(`&lt;`→`<`)를 보게 한다.
      const inner = decodeEntities(innerTex(token).trim());
      if (!isMathToken(token, inner)) {
        return token; // 통화형 `$...$` 등은 그대로 텍스트로 남긴다.
      }
      return `<imath data-tex="${escapeAttr(inner)}" data-display="${isDisplay(token) ? "1" : "0"}"></imath>`;
    });
  }
  return segments.join("");
}

/** transform에서 `<imath>` element를 MathJax로 렌더링한다. */
export function renderInlineMathElement(
  element: DomElement,
): React.ReactElement {
  const attribs = (element.attribs || {}) as Record<string, string>;
  const tex = attribs["data-tex"] || "";
  const display = attribs["data-display"] === "1";
  return <EquationRenderer tex={tex} inline={!display} />;
}
